// Deal Submission spec §7 — staff Deal Staging review queue.
// Lists every submission in Submitted / InStaging with broker label,
// template, doc counts, and a click-through to the per-submission review
// page. Gated by DealSubmission.Review (every staff role holds it).

import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { BrokerRecordLink, PortalAccountLink } from "@/components/crosslinks/BrokerCrossLinks";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { useAuth } from "@/hooks/useAuth";
import { useAssignableUsers } from "@/hooks/useAssignableUsers";
import { useDateFormatter } from "@/utils/datetime";
import {
  dealStagingApi,
  type DealStagingListItem,
  type DealSubmissionOrigin,
  type DealSubmissionStatus,
} from "@/api/dealStaging";
import { isEscalation, stagingCopyFor } from "./stagingOriginCopy";
import { stagingKeys } from "./stagingKeys";

const UNASSIGNED = "__unassigned__";

type StatusFilter = "" | DealSubmissionStatus;

/** "" is the default and sends no `origin` param — the backend then returns
 *  both origins, which is the point of keeping them in one queue. */
type OriginFilter = "" | DealSubmissionOrigin;

/** Labels come from `stagingCopyFor` so the filter option and the Origin
 *  column can never name the same thing two different ways. */
const ORIGIN_OPTIONS: readonly DealSubmissionOrigin[] = [
  "BrokerPortal",
  "BuyerSpecialistEscalation",
];

function StatusBadge({ status }: { status: DealSubmissionStatus }) {
  switch (status) {
    case "Submitted":
      return <Badge status="info">Submitted</Badge>;
    case "InStaging":
      return <Badge status="review">In staging</Badge>;
    case "ReturnedForChanges":
      return <Badge status="info">Returned</Badge>;
    case "Approved":
      return <Badge status="finalized">Approved</Badge>;
    case "Imported":
      return <Badge status="finalized">Imported</Badge>;
    case "Declined":
      return <Badge status="archived">Declined</Badge>;
    case "Withdrawn":
      return <Badge status="archived">Withdrawn</Badge>;
    default:
      return <Badge status="info">{status}</Badge>;
  }
}

/**
 * Which door one submission came in through. The queue is MIXED, so this
 * resolves per row — never once for the page.
 *
 * Asymmetric on purpose: broker-portal rows are the overwhelming majority, so
 * they render as muted text and recede, and the rare escalation gets a chip
 * that catches the eye. Badging every row would turn the column into noise and
 * bury the thing worth spotting. An unrecognised origin falls through to the
 * muted branch, matching `stagingCopyFor`'s broker-portal fallback.
 */
function OriginCell({ origin }: { origin: DealSubmissionOrigin }) {
  const label = stagingCopyFor(origin).originLabel;
  return isEscalation(origin) ? (
    <Badge status="info">{label}</Badge>
  ) : (
    <span className="text-small text-gray-mid">{label}</span>
  );
}

function liaisonLabel(row: DealStagingListItem) {
  return row.assignedReviewerUserId && row.assignedReviewerDisplayName
    ? row.assignedReviewerDisplayName
    : "Unassigned";
}

export function DealStagingQueuePage() {
  useBreadcrumbs([
    { label: "Deals", href: "/deal-flow/pipeline" },
    { label: "Staging and Review" },
  ]);
  const { user } = useAuth();
  const { dateTime } = useDateFormatter();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [originFilter, setOriginFilter] = useState<OriginFilter>("");
  const [search, setSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [mine, setMine] = useState(false);

  const c1Users = useAssignableUsers("C1");

  const effectiveOwner = mine && user?.id ? user.id : ownerFilter || undefined;

  const list = useQuery({
    queryKey: stagingKeys.list({ statusFilter, originFilter, search, effectiveOwner, mine }),
    queryFn: () =>
      dealStagingApi.list({
        status: statusFilter || undefined,
        q: search.trim() || undefined,
        assignedReviewerUserId: effectiveOwner,
        mine,
        origin: originFilter || undefined,
      }),
    // Live triage queue — opt out of the 5-minute global staleTime.
    staleTime: 60_000,
  });

  const rows = list.data ?? [];

  const columns: Column<DealStagingListItem>[] = [
    {
      key: "title",
      header: "Submission",
      render: (s) => (
        <Link
          to={`/deal-flow/deal-staging/${s.submissionId}`}
          className="font-semibold text-onyx hover:text-gold-shadow"
        >
          <div className="flex flex-col">
            <span>{s.title ?? "(untitled)"}</span>
            <span className="text-caption text-gray-mid font-normal truncate max-w-md">
              {s.templateName} v{s.templateVersion}
            </span>
          </div>
        </Link>
      ),
    },
    {
      key: "origin",
      header: "Origin",
      render: (s) => <OriginCell origin={s.origin} />,
    },
    {
      key: "broker",
      header: "Broker",
      render: (s) => (
        <div className="flex flex-col gap-[2px]">
          <BrokerRecordLink
            brokerId={s.brokerId}
            label={s.brokerName ?? "View broker record"}
            className="text-small text-onyx hover:text-gold-shadow hover:underline"
          />
          <PortalAccountLink portalUserId={s.brokerPortalUserId} className="text-caption text-gold-shadow hover:underline" />
        </div>
      ),
    },
    {
      key: "deal",
      header: "Pipeline deal",
      render: (s) =>
        s.pipelineDealId ? (
          <Link
            to={`/deal-flow/deals/${s.pipelineDealId}`}
            className="font-mono text-caption text-gold-shadow hover:text-gold-highlight"
          >
            {s.pipelineDealNumber ?? "View deal"}
          </Link>
        ) : (
          <span className="text-caption text-gray-mid italic">Not linked</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (s) => (
        <span className="inline-flex flex-wrap items-center gap-space-1">
          <StatusBadge status={s.status} />
          {s.unfiledResponseCount > 0 && (
            <Badge status="gold" data-testid={`broker-responded-${s.submissionId}`}>
              {stagingCopyFor(s.origin).respondedBadge}
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: "liaison",
      header: "Broker Liaison",
      render: (s) => (
        <span className={s.assignedReviewerUserId ? "text-small text-onyx" : "text-caption text-gray-mid italic"}>
          {liaisonLabel(s)}
        </span>
      ),
    },
    {
      key: "docs",
      header: "Documents",
      render: (s) => (
        <span className="text-small text-onyx">
          {s.documentCount}
          {s.flaggedDocumentCount > 0 && (
            <span className="ml-1 text-status-error">
              · {s.flaggedDocumentCount} flagged
            </span>
          )}
        </span>
      ),
    },
    {
      key: "submitted",
      header: "Submitted",
      render: (s) => (
        <span className="text-small text-onyx">
          {s.submittedAt ? dateTime(s.submittedAt) : "—"}
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader eyebrow="Deal Flow" title="Staging and Review" />
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-space-3 rounded-md border border-border-light bg-white-pure p-space-3">
        <label className="text-small text-onyx">
          Search
          <input
            className="mt-space-1 w-full border border-border-light rounded-sm px-space-2 py-space-1 text-small"
            placeholder="Business, broker, deal #…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label className="text-small text-onyx">
          Status
          <select
            className="mt-space-1 w-full border border-border-light rounded-sm px-space-2 py-space-1 text-small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="">Active (Submitted + In staging)</option>
            <option value="Submitted">Submitted</option>
            <option value="InStaging">In staging</option>
            <option value="ReturnedForChanges">Returned for changes</option>
            <option value="Approved">Approved</option>
          </select>
        </label>
        <label className="text-small text-onyx">
          Origin
          <select
            className="mt-space-1 w-full border border-border-light rounded-sm px-space-2 py-space-1 text-small"
            value={originFilter}
            onChange={(e) => setOriginFilter(e.target.value as OriginFilter)}
          >
            <option value="">All origins</option>
            {ORIGIN_OPTIONS.map((o) => (
              <option key={o} value={o}>{stagingCopyFor(o).originLabel}</option>
            ))}
          </select>
        </label>
        <label className="text-small text-onyx">
          Broker Liaison
          <select
            className="mt-space-1 w-full border border-border-light rounded-sm px-space-2 py-space-1 text-small"
            value={ownerFilter}
            disabled={mine}
            onChange={(e) => setOwnerFilter(e.target.value)}
          >
            <option value="">All liaisons</option>
            <option value={UNASSIGNED}>Unassigned</option>
            {(c1Users.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>{u.displayName}</option>
            ))}
          </select>
        </label>
        <label className="mt-auto inline-flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            checked={mine}
            disabled={!user?.id}
            onChange={(e) => setMine(e.target.checked)}
          />
          Mine ({user?.displayName ?? "current user"})
        </label>
      </div>
      <DataTable<DealStagingListItem>
        columns={columns}
        rows={rows}
        loading={list.isLoading}
        rowKey={(r) => r.submissionId}
        emptyMessage="No submissions match this view."
      />
    </div>
  );
}
